import ApiConfiguration from "./apiConfiguration";
import { Mode } from "../redux/reducers/modesReducer/modesModel";
import moment from "moment";
import { App, Description } from "../redux/reducers/appsReducer/appsModel";
import { AppFilterType } from "../routes/apps";
import { getCaregiverService } from "./api-helper";

import { handleApiError } from "./error-handlers";
import { CellularInfo, DeviceType, DeviceType1, ModeResponse, SubscriptionAddOnResponseDto, SupportedCurrency } from "../openapi";
import { LabelValueOption } from "@/types";
import { Routine } from "@/redux/reducers/routinesReducer/routinesModel";
import { TFunction } from "i18next";

export const phoneRegex = new RegExp(/^\+?\*?[0-9()]+$/, "g");

// we will compare with this version to determine wether to show or hide the mode override dropdown
export const DESKTOP_VERSION_FOR_MODE_OVERRIDE = "2.3.0";

// we will compare with this version to determine wether to show or hide google play store
export const DESKTOP_VERSION_FOR_GPS = "2.25.1";

// we will compare with this version to determine wether to show or hide geofencing
export const DESKTOP_VERSION_FOR_GEOFENCING = "2.27.0";

// we will compare with this version to determine wether to show or hide geofencing
export const CAREGIVER_NATIVE_VERSION_FOR_SKIP_BILLING_IOS = "1.5.2";

// we will compare with this version to determine wether to show or hide the app time limits for watch
export const DESKTOP_VERSION_FOR_WATCH_APP_TIME_LIMITS = "1.8.1";

// we will compare with this version to determine wether to show app store review in feedback modal
export const NATIVE_APP_MIN_VERSION_FOR_FEEDBACK = "1.6.2";

// minimum native app version that supports the ATA wizard - update when the feature ships
export const NATIVE_APP_MIN_VERSION_FOR_ATA = "1.7.0";

// check if the browser is safari
export const isSafari = (navigator.userAgent.search("Safari") > -1 ||
navigator.userAgent.search("iPhone") > -1) &&
navigator.userAgent.search("Chrome") < 0;

const ua = window.navigator.userAgent;
export const isIos = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);

export const isAndroidMobile = /android/i.test(ua) && /mobile/i.test(ua) && navigator.maxTouchPoints > 0;

export const platform = isIos ? "ios" : isAndroidMobile ? "android" : "desktop";

const webkit = !!ua.match(/WebKit/i);
export const isIosSafari = isIos && webkit && !ua.match(/CriOS/i);

const deviceType = (() => {
  return window?.isNative ? DeviceType.Mobile : DeviceType.Browser;
})();

export const isIosApp = deviceType === DeviceType.Mobile && isIosSafari;

const analyticsDeviceType =
  deviceType === DeviceType.Mobile ? DeviceType1.MobileApp : /Mobi/i.test(window?.navigator?.userAgent) ? DeviceType1.MobileWeb : DeviceType1.Desktop;

const convertFrom24To12Format = (time24: string) => {
  const matched = time24.match(/([0-9]{1,2}):([0-9]{2})/);
  // to avoid error while using keyboard on time input
  if (!matched) {
    return null;
  } else {
    const [sHours, minutes] = matched.slice(1);
    const period = +sHours < 12 || +sHours === 24 ? "AM" : "PM";
    const hours = +sHours % 12 || 12;

    return `${hours}:${minutes} ${period}`;
  }
};

const trackEvent = async (event: string, email: string, eventData?: unknown) => {
  console.log(event, email, eventData);
};

function appTimeConvert(num: number) {
  const hours = Math.floor(num / 60);
  const minutes = num % 60;
  let totalTime = "";
  if (hours > 0) {
    totalTime = `${hours} Hr `;
  }
  if (minutes > 0) {
    totalTime = totalTime + `${minutes} mins`;
  }
  return totalTime;
}

const isNameIsANumber = (name: string) => {
  return !isNaN(parseInt(name, 10));
};

const isPwCircleCode = (phoneNumber: string): boolean => /^\d{6}$/.test(phoneNumber.trim());

// 3-digit codes starting with "9" (e.g. 911, 988) are emergency short codes, not real numbers.
const isEmergencyShortCode = (phoneNumber: string): boolean => /^9\d{2}$/.test(phoneNumber.replace(/\D/g, ""));

// A "full" phone number is one that counts toward the FF real-number contact limit.
// PW circle codes and emergency short codes are excluded; everything else counts.
export const isFullPhoneNumber = (phoneNumber: string): boolean =>
  !isPwCircleCode(phoneNumber) && !isEmergencyShortCode(phoneNumber);

const getCognitoAPIWrapperURL = () => {
  return import.meta.env.VITE_ENV === "production"
    ? "https://4s92h8dt3i.execute-api.us-east-2.amazonaws.com/prod/"
    : "https://fsla6tjmoa.execute-api.us-east-2.amazonaws.com/dev/";
};

const isAppEnabled = (appId: string, modes: Mode[]): boolean => {
  if (modes) {
    return modes.some(mode => {
      const currentApp = mode.appIds.filter(a => a === appId);
      return currentApp.length > 0;
    });
  }
  return false;
};

function generateArrayOfYears() {
  const max = new Date().getFullYear();
  const min = max - 100;
  const years: number[] = [];

  for (let i = max; i >= min; i--) {
    years.push(i);
  }
  return years;
}

function generateArrayOfMonths() {
  return ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
}

let myServiceConfig: ApiConfiguration;
const getServiceConfig = async () => {
  if (!myServiceConfig) {
    myServiceConfig = new ApiConfiguration();
  }
  return myServiceConfig;
};


const createNewLine = (a: string) => {
  const b = a.split(" ");
  let c = "";
  b.forEach((item, index) => {
    if (index % 2 === 0) {
      c += item + " ";
    } else {
      c += item + "\n";
    }
  });
  return c;
};

const shortTimeFormat = (ts: Date) => {
  const formattedTime = moment(ts).fromNow();
  const customFormattedTime = formattedTime
    .replace("minutes", "min")
    .replace("minute", "min")
    .replace("hours", "hr")
    .replace("hour", "hr")
    .replace("seconds", "sec")
    .replace("an", "1")
    .replace("second", "sec");
    return customFormattedTime
};

const mintTohrs = (mins: number) => {
  const minsHour = moment
    .duration(Math.round(mins), "minutes")
    .toString()
    .replace("PT", "")
    .replace("H", "_")
    .replace("M", "");
  const n = minsHour.split("_");
  if (n.length > 1) {
    const _hr = parseInt(n[0]) > 1 ? " hrs" : " hr";
    return n[0] + _hr + "\n" + n[1] + " mins ";
  } else {
    if (n[0] != "P0D") {
      return n[0] + " mins";
    } else {
      return ""
    }
  }
};

// Passed back to a picker holding unparseable input so it doesn't reset the field to the last valid time
const INVALID_DATE = new Date(NaN);

const hoursMinutesToDate = (hourAndDate?: string | null) => {
  const segments = hourAndDate?.split(":") || [];
  if (segments.length !== 2) {
    return null;
  }
  const hours = Number(segments[0]);
  const minutes = Number(segments[1]);

  if (hours == 24) {
    return new Date(`1970-01-02T00:${padToTwoPlaces(minutes)}`);
  }
  return new Date(`1970-01-01T${padToTwoPlaces(hours)}:${padToTwoPlaces(minutes)}`);
}

const dateToHoursMinutes = (timeString: string | null, midnightIsTomorrow = false) => {
  const newDate = hoursMinutesToDate(timeString);
  if (newDate === null) {
    return null;
  }
  let hours = newDate.getHours();
  const minutes = newDate.getMinutes();
  if (hours === 0 && minutes === 0 && midnightIsTomorrow) {
    hours = 24;
  }
  return padToTwoPlaces(hours) + ":" + padToTwoPlaces(minutes);
}

const padToTwoPlaces = (n: number) => {
  return (n <= 9) ? "0" + n : n;
}

const filterApps = (apps:App[],appsFilterOption:AppFilterType,appDescriptions:Description[] | undefined)=>{
  if (apps.length>0) {
    let sortApps: App[] = [...apps].sort((a1, a2) => {
      if (appsFilterOption.sortByOption === "alphabetical") {
        return a1.name.localeCompare(a2.name);
      } else {
        return (a2.downloads ? a2.downloads : 0) - (a1.downloads ? a1.downloads : 0);
      }
    });

    if (appsFilterOption.appLevels.length > 0) {
      sortApps = sortApps?.filter(app => {
        if (app.level && appsFilterOption.appLevels.includes(app.level)) {
          return app;
        }
      });
    }

    if(appsFilterOption.freeApps) {
      const freeAppsIds = appDescriptions?.filter((elm)=>{
        if(elm.label.includes("Free")){
          return elm;
        }
      }).map((e)=>{
        return e.id
      })
      sortApps = sortApps?.filter(app => {
        if("appDescriptionIds" in app){
           return app.appDescriptionIds?.filter((desId)=>{
              return  freeAppsIds?.includes(desId)
            })
        }
      });
    }
    return sortApps
  }
}
const trackPage = async (route:string) =>{
  try {
    const caregiverService = await getCaregiverService();
    await caregiverService.savePageVisit({route:route, deviceType: analyticsDeviceType, userAgent: window.navigator.userAgent})
  } catch (error) {
    handleApiError(error);
  } 
}

const isValidEmail = (email: string) => {
  // https://stackoverflow.com/a/46181
  return email
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const fixPredefinedAllModeAppsRlt = async (modes: ModeResponse[]) => {
  if (localStorage.getItem("predefinedAllModeAppsRltFixed")) {
    return modes;
  }
  let predefinedAllModeIndex = -1;
  const enabledAppIds = new Set<string>();
  // Loop through modes to find the index of predefined-all-mode
  // and prepare unique list (set) of all appIds assigned to modes other than predefined-all-mode.
  modes.forEach((mode, index) => {
    if (mode.isPredefined && mode.name.toLowerCase() === "all mode") {
      if (predefinedAllModeIndex === -1) {
        predefinedAllModeIndex = index;
      }
    } else {
      mode.appIds.forEach(id => enabledAppIds.add(id));
    }
  });

  // Then find which enabled appIds are missing from the predefined-all-mode.
  if (predefinedAllModeIndex !== -1) {
    const predefinedAllModeAppIds = new Set(modes[predefinedAllModeIndex].appIds);
    const predefinedAllModeMissingAppIds: string[] = [];
    enabledAppIds.forEach(appId => {
      if (!predefinedAllModeAppIds.has(appId)) {
        predefinedAllModeMissingAppIds.push(appId);
      }
    });
    // Assign missing appIds (if any) to predefined-all-mode.
    if (predefinedAllModeMissingAppIds.length > 0) {
      modes[predefinedAllModeIndex].appIds.push(...predefinedAllModeMissingAppIds);
      const caregiverService = await getCaregiverService();
      caregiverService.modifyModeAppRelations({
        modeId: modes[predefinedAllModeIndex].id,
        addIds: predefinedAllModeMissingAppIds
      });
    }
  }
  localStorage.setItem("predefinedAllModeAppsRltFixed", "true");
  return modes;
}

const compareDesktopVersion = (versionString: string, compareVersion: string) => {
  const splittedArray = versionString.split(".");
  const currentVersion = `${splittedArray[0]}.${splittedArray[1]}.${splittedArray[2]}`;
  return currentVersion.localeCompare(compareVersion, undefined, { numeric: true }) >= 0;
}

const MAIN_TITLE = "Caregiver Admin - Pinwheel";

const setTitle = (title: string) => {
  title ? (document.title = title + " | " + MAIN_TITLE) : (document.title = MAIN_TITLE);
};

const appTimeLimitOptions: LabelValueOption<number>[] = [
  {
    label: "Unlimited",
    value: 24*60
  },
  {
    label: "10 minutes",
    value: 10
  },
  {
    label: "20 minutes",
    value: 20
  },
  {
    label: "30 minutes",
    value: 30
  },
  {
    label: "40 minutes",
    value: 40
  },
  {
    label: "50 minutes",
    value: 50
  },
  {
    label: "1 hour",
    value: 60
  },
  {
    label: "1 hour 10 minutes",
    value: 70
  },
  {
    label: "1 hour 20 minutes",
    value: 80
  },
  {
    label: "1 hour 30 minutes",
    value: 90
  },
]

const getReactNativeDeviceInfo = () => {
  if (window?.isNative && window?.rnDeviceInfo) {
    return window.rnDeviceInfo;
  }
};

const getReactNativeOS = () => {
  const rnDeviceInfo = getReactNativeDeviceInfo();
  if (rnDeviceInfo) {
    return rnDeviceInfo.osName.toLowerCase();
  }
};

const getReactNativeAppVersion = () => {
  const rnDeviceInfo = getReactNativeDeviceInfo();
  if (rnDeviceInfo) {
    return rnDeviceInfo.appVersion;
  }
};

const hashCode = (string: string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
      const code = string.charCodeAt(i);
      hash = ((hash<<5)-hash)+code;
      hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}

enum CurrencySymbols {
  Usd = "$",
  Gbp = "£",
  Cad = "C$",
  Aud = "A$"
}

const formatPrice = (couponApplied: boolean, currency?: SupportedCurrency, amount?: number) => {
  let finalAmount = "";
  const amountString = couponApplied ? "0.00" : amount ? (amount / 100).toFixed(2) : "0.00";
  if (currency) {
    if (currency === SupportedCurrency.Cad) {
      finalAmount += `${CurrencySymbols.Cad}${amountString}`;
    } else if (currency === SupportedCurrency.Gbp) {
      finalAmount += `${CurrencySymbols.Gbp}${amountString}`;
    } else if (currency === SupportedCurrency.Aud) {
      finalAmount += `${CurrencySymbols.Aud}${amountString}`;
    } else {
      finalAmount += `${CurrencySymbols.Usd}${amountString}`;
    }
  }
  return finalAmount;
}

const convertBytesToGB = (bytes: number) => {
    const gb = bytes / (1000 * 1000 * 1000);
    return Number.isInteger(gb) ? gb.toString() : gb.toFixed(2);
};

const convertSecondsToMinutes = (seconds: number) => {
  const sec = typeof seconds === "string" ? parseFloat(seconds) : seconds;
  if (!isNaN(sec) && sec >= 0) {
    const minutes = sec / 60;
    return Number.isInteger(minutes) ? minutes.toString() : minutes.toFixed(2);
  }
  return undefined;
};

type ServiceType = "data" | "voice" | "sms";
interface Allowance { value?: number; unlimited?: boolean; }

const calculateTotalAllowances = (
  cellularInfo: CellularInfo,
  subscriptionAddOns: SubscriptionAddOnResponseDto[],
  serviceType: ServiceType,
): Allowance | undefined => {
  const initialValue =
    serviceType === "data"
      ? cellularInfo.data?.initial
      : serviceType === "voice"
      ? cellularInfo.voice?.initial
      : serviceType === "sms"
      ? cellularInfo.sms?.initial
      : undefined;

  if (initialValue === undefined) return undefined;

  let total = initialValue;

  for (const addon of subscriptionAddOns) {
    if (serviceType === "data" && addon.allowances.dataBytes)
      total += addon.allowances.dataBytes;
    if (serviceType === "voice" && addon.allowances.voiceSeconds)
      total += addon.allowances.voiceSeconds;
    if (serviceType === "sms" && addon.allowances.smsMessages)
      total += addon.allowances.smsMessages;
  }

  return { value: total };
};

const toCamelCase = (str: string) => {
    return str
      .replace(/[^a-zA-Z0-9 ]/g, "")    // remove special characters
      .toLowerCase()
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) =>
        index === 0 ? match.toLowerCase() : match.toUpperCase()
      )
      .replace(/\s+/g, "");             // remove spaces
};

const transformedRoutines = (routines: Routine[], t: TFunction) =>
  routines?.map((routine) => {
    const key = toCamelCase(routine.name);
    const name = t(key, { defaultValue: undefined });
  
    return {
      ...routine,
      name: name !== undefined && name !== key ? name : routine.name,
      tasks: routine.tasks.map((task) => {
        const taskKey = toCamelCase(task);
        const taskName = t(taskKey, { defaultValue: undefined });
        return taskName !== undefined && taskName !== taskKey ? taskName : task;
      }),
    };
  });

export {
  deviceType,
  analyticsDeviceType,
  convertFrom24To12Format,
  trackEvent,
  getCognitoAPIWrapperURL,
  isAppEnabled,
  getServiceConfig,
  generateArrayOfYears,
  generateArrayOfMonths,
  mintTohrs,
  createNewLine,
  filterApps,
  trackPage,
  isValidEmail,
  hoursMinutesToDate,
  dateToHoursMinutes,
  INVALID_DATE,
  appTimeConvert,
  fixPredefinedAllModeAppsRlt,
  compareDesktopVersion,
  setTitle,
  shortTimeFormat,
  appTimeLimitOptions,
  getReactNativeOS,
  getReactNativeAppVersion,
  isNameIsANumber,
  isPwCircleCode,
  hashCode,
  formatPrice,
  convertBytesToGB,
  convertSecondsToMinutes,
  calculateTotalAllowances,
  transformedRoutines,
};
